import * as React from 'react';
import * as moment from 'moment';
import { __ } from 'react-i18n';
import { IconType } from '../components/Zemplin/General/SvgIcon';
import { phonePrefixes } from '../components/Dsi/Cart/utilities';
import { getCartItemInfo } from '../utilities/selectors';

export const formatPrice = (
  price: number,
  currency: string,
  decimalPlaces: number = 2,
  zeroPriceText: boolean = false,
  thousandDelimeter: string = '',
  giftPriceText: boolean = false,
  forceDecimalPlaces: number = -1,
) => {
  if (!price && price !== 0 && !zeroPriceText) {
    return '';
  }
  if (typeof price === 'string') {
    price = parseFloat(price);
  }

  let priceValue: string;
  if (currency === 'CZK' || currency === 'Kč') {
    decimalPlaces = 0;
    thousandDelimeter = ' ';
  }

  if (forceDecimalPlaces !== -1) {
    decimalPlaces = forceDecimalPlaces;
  }

  if (Number.isInteger(price)) {
    priceValue =
      decimalPlaces > 0 ? `${price},${'0'.repeat(decimalPlaces)}` : `${price}`;
  } else {
    priceValue = round(price, decimalPlaces)
      .toFixed(decimalPlaces)
      .toString()
      .replace('.', ',');
    // priceValue = price
    // .toFixed(decimalPlaces)
    // .replace('.', ',')
    // .toString();
  }

  if (thousandDelimeter) {
    priceValue = priceValue.replace(/\B(?=(\d{3})+(?!\d))/g, thousandDelimeter);
  }

  if (zeroPriceText && price === 0) {
    return __('Zadarmo');
  }

  if (giftPriceText && price === 0) {
    return __('Darček');
  }

  switch (currency) {
    case 'EUR': {
      return (
        <>
          {priceValue}
          &nbsp;€
        </>
      );
    }
    case 'CZK': {
      return (
        <>
          {priceValue}
          &nbsp;Kč
        </>
      );
    }
    default: {
      return (
        <>
          {priceValue}
          &nbsp;{currency}
        </>
      );
    }
  }
};

export const formatPriceToString = (
  price: number | string,
  currency: string,
) => {
  return `${price} ${currency}`;
};

export const getSymbolFromCurrency = (currency: string): string => {
  switch (currency) {
    case 'CZK':
      return 'Kč';

    case 'EUR':
      return '€';

    default:
      return '€';
  }
};

export const getCountryFromId = (country: string): string => {
  switch (country) {
    case 'sk':
      return 'Slovensko';

    case 'cz':
      return 'Česká republika';

    default:
      return country;
  }
};

export const checkMetaTag = tag => {
  if (typeof tag === 'string') {
    return tag;
  } else {
    return '';
  }
};

export const prop = (object, path, defaultValue: any = null) => {
  const val = path.split('.').reduce((o, x) => {
    return typeof o === 'undefined' || o === null ? defaultValue : o[x];
  }, object);
  if (typeof val === 'undefined') {
    return defaultValue;
  }
  return val;
};

export const emailIsValid = email => {
  const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.length > 0 && emailRegex.test(email.toLowerCase());
};

export const formatDate = (
  date: Date,
  format: string = 'YYYY-MM-DD HH:mm:ss',
): string => {
  return moment(date).format(format);
};

export const getRequestColor = (status, theme): string => {
  switch (status) {
    case 'WAITING':
      return theme.colors.placeholder;

    case 'INPROGRESS':
      return theme.colors.primary;

    case 'DONE':
      return theme.colors.stockCountAvailable;

    default:
      return theme.colors.textPrimary;
  }
};

export const getRequestIcon = (status): IconType => {
  switch (status) {
    case 'WAITING':
      return IconType.time;

    case 'INPROGRESS':
      return IconType.inwork;

    case 'DONE':
      return IconType.paid;

    default:
      return IconType.inwork;
  }
};

export const getOrderColor = (status, theme): string => {
  switch (status) {
    case 'Zrušená':
      return theme.colors.dangerColor;

    case 'Prijatá':
      return theme.colors.successColor;

    case 'Pripravuje sa':
      return theme.colors.textPrimary;

    default:
      return theme.colors.textPrimary;
  }
};

export const getDeliveryInfoFromAddress = (
  id: number,
  addresses: any[],
): any => {
  const address = addresses.filter(a => a.id.toString() === id.toString());
  return address[0];
};

export const arrayToObject = (array, key) =>
  array.reduce((obj, item) => {
    obj[item[key]] = item;
    return obj;
  }, {});

export const getImagePath = (
  baseUrl: string,
  filename,
  { width, height } = { width: 64, height: 80 },
  noSize = false,
  webp = false,
  typeCover = false,
) => {
  if (height === 0) {
    return `${baseUrl}/image?query=${filename}&width=${width}&webp=${
      webp ? 1 : 0
    }${typeCover ? '&type=cover' : ''}`;
  }

  return noSize
    ? `${baseUrl}/image?query=${filename}&webp=${webp ? 1 : 0}${
        typeCover ? '&type=cover' : ''
      }`
    : `${baseUrl}/image?query=${filename}&width=${width}&height=${height}&webp=${
        webp ? 1 : 0
      }${typeCover ? '&type=cover' : ''}`;
};

export function round(num, precision) {
  const a = +(Math.round(+(num + `e+${precision}`)) + `e-${precision}`);
  if (isNaN(a)) {
    return 0;
  }
  return a;
}

export function cookiesExpiresDate() {
  // expires in 1 year
  return new Date(new Date().setFullYear(new Date().getFullYear() + 1));
}

export function resolveBaseUrlByLang(hostname?: string, lang?: string): string {
  if (!hostname) {
    if (window && window.location && window.location.hostname) {
      hostname = window.location.hostname;
    } else {
      hostname = 'jbl';
    }
  }

  if (!lang) {
    if (hostname && hostname.includes('cz')) {
      lang = 'cz';
    } else {
      lang = 'sk';
    }
  }

  if (hostname) {
    if (hostname.includes('ariete')) {
      // we are returning other url - if lang is cz we return sk url
      if (lang === 'cz') {
        return 'https://www.ariete.cz/';
      } else {
        return 'https://www.ariete.sk/';
      }
    } else if (hostname.includes('laica')) {
      // we are returning other url - if lang is cz we return sk url
      if (lang === 'cz') {
        return 'https://www.laicaitaly.cz/';
      } else {
        return 'https://www.laica.sk/';
      }
    } else if (hostname.includes('kenwood')) {
      if (lang === 'cz') {
        return 'https://www.kenwood.cz/';
      } else {
        return 'https://www.kenwood-electronics.sk/';
      }
    } else {
      if (lang === 'cz') {
        return 'https://www.jbl.cz/';
      } else {
        return 'https://www.jbl.sk/';
      }
    }
  }

  return 'https://www.jbl.sk/';
}

export function resolveProductInSlovakBasedOnCount(count: number): string {
  if (count > 4) {
    return __('produktov');
  }

  switch (count) {
    case 0:
      return __('produktov');

    case 1:
      return __('produkt');

    case 2:
    case 3:
    case 4:
      return __('produkty');

    default:
      return __('produktov');
  }
}

export const resolveGiftInSlovakBasedOnCount = (count: number): string => {
  if (count > 4) {
    return __('darčekov');
  }

  switch (count) {
    case 0:
      return __('darčekov');

    case 1:
      return __('darček');

    case 2:
    case 3:
    case 4:
      return __('darčeky');

    default:
      return __('darčekov');
  }
};

export const resolveCheapestInSlovakBasedOnCount = (count: number): string => {
  if (count > 4) {
    return __('najlacnejších');
  }

  switch (count) {
    case 0:
      return __('najlacnejších');

    case 1:
      return __('najlacnejší');

    case 2:
    case 3:
    case 4:
      return __('najlacnejšie');

    default:
      return __('najlacnejších');
  }
};

export const resolveMostExpensiveInSlovakBasedOnCount = (
  count: number,
): string => {
  if (count > 4) {
    return __('najdrahších');
  }

  switch (count) {
    case 0:
      return __('najdrahších');

    case 1:
      return __('najdrahší');

    case 2:
    case 3:
    case 4:
      return __('najdrahšie');

    default:
      return __('najdrahších');
  }
};

export const createAbsoluteUrl = (url: string): string => {
  if (
    url &&
    typeof url === 'string' &&
    !url.startsWith('/') &&
    !url.startsWith('\\') &&
    !url.startsWith('http')
  ) {
    return '/' + url;
  }
  return url;
};

export function withoutVat(price: number, vat: number): number {
  return price / (1 + vat / 100);
}

export function addVat(price: number, vat: number): number {
  return price * (1 + vat / 100);
}

export const getPhonePrefix = (phone: string): string => {
  if (phone && phonePrefixes && phonePrefixes.length) {
    const currentPrefix = phonePrefixes.find(prefix =>
      phone.startsWith(prefix),
    );
    if (currentPrefix) {
      return currentPrefix;
    }
  }

  return '';
};

export const getPhoneWithoutPrefix = (phone: string): string => {
  if (phone && phonePrefixes && phonePrefixes.length) {
    const currentPrefix = phonePrefixes.find(prefix =>
      phone.startsWith(prefix),
    );
    if (currentPrefix) {
      return phone.slice(currentPrefix.length);
    }
  }
  return phone;
};

export const sendCartToEcomail = (cart) => {
  try{
    if((window as any)?.ecotrack){
 
      console.log("ECO");
      console.log({
        schema: '',
            data: {
                action: 'Basket',
                products: cart.items.slice(0,10).map(item => { 
                  const {
                    name,
                    image,
                    finalPrice,
                    productUrl,
                  } = getCartItemInfo(item);
            
                  return {
                    productId: item.product.product_id,
                    img_url: image,
                    url: productUrl,
                    name: name,           
                    price: round(finalPrice,cart.lang === 'cz'?0:2),
                    description: item.product?.description
                  }
                })
            }
      });

      (window as any).ecotrack('trackUnstructEvent', {
        schema: '',
            data: {
                action: 'Basket',
                products: cart.items.slice(0,10).map(item => { 
                  const {
                    name,
                    image,
                    finalPrice,
                    productUrl,
                  } = getCartItemInfo(item);
            
                  return {
                    productId: item.product.product_id,
                    img_url: image,
                    url: productUrl,
                    name: name,           
                    price: round(finalPrice,cart.lang === 'cz'?0:2),
                    description: item.product?.description
                  }
                })
            }
      });
    } 
  } catch(e){
    console.error('Error sending Basket trackUnstructEvent to ecomail');
  }
}

export const prefixUrl = (prefix, url) => {
  const trimmedPrefix = prefix.replace(/\/$/, '');
  const trimmedUrl = url.replace(/^\/+/, '');
  return `${trimmedPrefix}/${trimmedUrl}`;
};



